import React from 'react';
import { Box, ImageListItem, ImageListItemBar } from '@mui/material';
import { PdfThumbnail } from '../../thumbnails/pdfThumbnail';
import { ThumbnailLoadingOverlay } from '../../thumbnails/common';
import { WordThumbnail } from '../../thumbnails/wordThumbnail';
import { ExcelThumbnail } from '../../thumbnails/excelThumbnail';
import { GeneratedExtensionIcon } from '../../../gallery/fileExtensions/generatedExtensionIcon';
import type { DisplayedFile } from '../../../gallery/filesStore';
import { fileKindService } from 'services/fileService';
import { isPersistedSource } from 'shared/utils/sourceUrl';
import {
  getListItemSx,
  listImageAnimatedActionIconSx,
  listImageBarActionsBottomSx,
  listImageBarSx,
  listImageBarFullTitleSx,
  listImageIconContainerSx,
  focusedThumbnailSx,
  selectedThumbnailOutlineSx,
  selectedThumbnailOverlaySx,
} from '../../styles';

interface GenericListItemProps {
  image: DisplayedFile;
  thumbnailSize: number;
  listItemRef: React.RefObject<HTMLLIElement>;
  onClick: (e: React.SyntheticEvent) => void;
  showActions: boolean;
  setShowActions: React.Dispatch<React.SetStateAction<boolean>>;
  actionIcon: React.ReactNode;
  description: string;
  showFullTitle: boolean;
  selected?: boolean;
  focused?: boolean;
  lazy?: boolean;
}

export const GenericListItem: React.FC<GenericListItemProps> = ({
  image,
  thumbnailSize,
  listItemRef,
  onClick,
  showActions,
  setShowActions,
  actionIcon,
  description,
  showFullTitle,
  selected = false,
  focused = false,
  lazy = false,
}) => {
  const isPdfNotConverted =
    fileKindService.isPdf(image) && !fileKindService.isConvertedPdf(image);
  const pdfThumbnailModel = isPdfNotConverted
    ? fileKindService.resolvePdfThumbnailModel(image)
    : null;
  const isWord = fileKindService.isWord(image);
  const isExcel = fileKindService.isExcel(image);
  const resolvedActionIcon = showFullTitle ? (
    <Box sx={listImageAnimatedActionIconSx(showActions)}>{actionIcon}</Box>
  ) : showActions ? (
    actionIcon
  ) : undefined;

  return (
    <ImageListItem
      ref={listItemRef}
      sx={[getListItemSx(thumbnailSize, true), selected && selectedThumbnailOutlineSx, focused && focusedThumbnailSx]}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {selected && <Box sx={selectedThumbnailOverlaySx} />}
      {pdfThumbnailModel?.kind === 'ready' ? (
        <PdfThumbnail
          src={pdfThumbnailModel.src}
          thumbnailSize={thumbnailSize}
          onClick={onClick}
          fileType={image.type}
          fileExtension={image.data?.extension}
          lazy={lazy}
          thumbnailCacheKey={pdfThumbnailModel.cacheKey}
        />
      ) : pdfThumbnailModel?.kind === 'loading' ? (
        <Box onClick={onClick} sx={listImageIconContainerSx}>
          <ThumbnailLoadingOverlay />
        </Box>
      ) : isWord && isPersistedSource(image.downloadSrc) ? (
        <WordThumbnail
          src={image.downloadSrc}
          thumbnailSize={thumbnailSize}
          onClick={onClick}
          fileType={image.type}
          fileExtension={image.data?.extension}
        />
      ) : isExcel && isPersistedSource(image.downloadSrc) ? (
        <ExcelThumbnail
          src={image.downloadSrc}
          thumbnailSize={thumbnailSize}
          onClick={onClick}
          fileType={image.type}
          fileExtension={image.data?.extension}
        />
      ) : (
        <Box onClick={onClick} sx={listImageIconContainerSx}>
          <GeneratedExtensionIcon
            fileType={image.type}
            fileExtension={image.data?.extension}
            size={48}
          />
        </Box>
      )}
      <ImageListItemBar
        title={description}
        actionIcon={resolvedActionIcon}
        onClick={onClick}
        sx={
          showFullTitle
            ? [listImageBarSx, listImageBarFullTitleSx, listImageBarActionsBottomSx]
            : listImageBarSx
        }
      />
    </ImageListItem>
  );
};
